// @flow

import React, { type Node } from 'react';
import { GET_HELP_PATH } from '@zapier/product-get-help';

const renderMessage = (status: ?number, messages: ?Array<string>): Node => {
  if (messages && messages.length > 0) {
    return <p>{messages.join(' ')}</p>;
  }

  if (!status) {
    return <p>Unfortunately, that’s all we know. Please try again.</p>;
  }

  if (status === 400) {
    return (
      <p>
        We got code 400, which means that our server can’t work with what we
        sent. Did you submit a form? Please make sure you have completed all
        required fields and try again.
      </p>
    );
  }
  if (status === 429) {
    return (
      <p>
        You've already submitted a ticket. Please wait a few moments before
        trying again.
      </p>
    );
  }
  if (status === 500) {
    return (
      <p>
        We got code 500, which means something is broken on our side. We’re
        already being alerted of this specific error and will fix it soon!
      </p>
    );
  }
  if (status === 502) {
    return (
      <p>
        We got code 502, which means our server is restarting. We’re probably
        pushing out some awesome new features! Just wait a minute or two and try
        again.
      </p>
    );
  }
  if (status === 504) {
    return (
      <p>
        We got code 504, which means our server didn’t respond in time. If this
        happened in the context of a Zap, please make sure nothing is blocking
        Zapier from connecting to the apps it uses and try again.
      </p>
    );
  }

  return (
    <p>
      Unfortunately, all we know is that the code was {status}. Please try
      again.
    </p>
  );
};

const defaultProps = {
  isFromContactForm: false,
};

type Props = {
  summary?: string,
  status?: number,
  messages?: Array<string>,
  isFromContactForm?: boolean,
};

const ErrorInfo = ({
  summary,
  status,
  messages,
  isFromContactForm,
}: Props): Node => {
  const contactFormErrorText = isFromContactForm
    ? ' If the error occurred on the contact form, please wait a while and try again.'
    : '';
  return (
    <div className="dashboard-modal error-modal">
      <h1 className="dashboard-modal__title dashboard-modal__title--small">
        {summary || 'We got an error on that!'}
      </h1>
      <div className="dashboard-modal__content dashboard-modal__content--error">
        {renderMessage(status, messages)}
        <p>
          Please{' '}
          {/* We used to publish our email address here for errors with the contact form, but we not longer allow direct email */}
          <a href={GET_HELP_PATH}>contact support</a> if this persists.
          {contactFormErrorText}
        </p>
      </div>
    </div>
  );
};

ErrorInfo.defaultProps = defaultProps;

export default ErrorInfo;
